import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SafeheirLogo from '../components/SafeheirLogo';

const S = '#00F5A0';
const G = '#8892A4';

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={S} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const plans = [
  {
    id: 'free',
    name: 'Gratuit',
    price: '0 €',
    period: 'pour toujours',
    highlight: false,
    badge: null,
    description: 'Démarrez sans engagement. Idéal pour tester SafeHeir.',
    cta: 'Commencer gratuitement',
    ctaRoute: '/register',
    features: [
      'Jusqu\'à 3 bénéficiaires',
      'Jusqu\'à 20 éléments dans le coffre',
      'Check-in mensuel',
      'Chiffrement AES-256',
      'Export de données (RGPD)',
    ],
    limitations: [
      'Pas de check-in hebdomadaire',
      'Pas de rappel email',
      'Support communautaire uniquement',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '4,99 €',
    period: 'par mois',
    highlight: true,
    badge: 'Recommandé',
    description: 'La solution complète pour protéger vos proches sans compromis.',
    cta: 'Essayer 30 jours gratuits',
    ctaRoute: '/register',
    features: [
      'Bénéficiaires illimités',
      'Coffre-fort illimité',
      'Check-in de 1 semaine à 1 an',
      'Rappels email automatiques',
      'Message personnalisé par bénéficiaire',
      'Chiffrement AES-256 + Zero Knowledge',
      'Export de données (RGPD)',
      'Support prioritaire',
    ],
    limitations: [],
  },
  {
    id: 'institution',
    name: 'Institutionnel',
    price: 'Sur devis',
    period: 'par organisation',
    highlight: false,
    badge: 'B2B',
    description: 'Pour notaires, conseillers patrimoniaux et mutuelles. Marque blanche disponible.',
    cta: 'Nous contacter',
    ctaRoute: null,
    ctaHref: 'mailto:contact@safeheir.fr?subject=Offre%20Institutionnelle',
    features: [
      'Tout du plan Premium',
      'Marque blanche / white-label',
      'SSO entreprise (SAML, OIDC)',
      'Tableau de bord partenaire',
      'API d\'intégration',
      'DPA et conformité RGPD inclus',
      'SLA garantis',
      'Accompagnement dédié',
    ],
    limitations: [],
  },
];

const PricingPage = () => {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: '100vh', background: '#0A1628', color: '#fff', padding: '0 0 80px' }}>

      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <SafeheirLogo size="md" />
        </Link>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Link to="/login" style={{ color: G, textDecoration: 'none', fontSize: 14 }}>Connexion</Link>
          <Link to="/register" style={{
            background: S, color: '#0A1628', textDecoration: 'none',
            fontSize: 13, fontWeight: 700, padding: '8px 16px', borderRadius: 8,
          }}>Essai gratuit</Link>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '64px 24px 0' }}>

        {/* Headline */}
        <div style={{ textAlign: 'center', marginBottom: 56 }}>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 800, margin: '0 0 16px', letterSpacing: '-0.03em' }}>
            Tarifs simples, <span style={{ color: S }}>sans surprise</span>
          </h1>
          <p style={{ color: G, fontSize: 16, maxWidth: 520, margin: '0 auto', lineHeight: 1.7 }}>
            Protégez vos proches dès aujourd'hui. Commencez gratuitement, évoluez quand vous en avez besoin.
          </p>
        </div>

        {/* Plans grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, alignItems: 'start' }}>
          {plans.map(plan => (
            <div key={plan.id} style={{
              position: 'relative',
              background: plan.highlight ? 'rgba(0,245,160,0.05)' : 'rgba(255,255,255,0.03)',
              border: plan.highlight ? `1px solid rgba(0,245,160,0.3)` : '1px solid rgba(255,255,255,0.08)',
              borderRadius: 20,
              padding: '32px 28px',
              boxShadow: plan.highlight ? '0 0 40px rgba(0,245,160,0.08)' : 'none',
            }}>
              {plan.badge && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: plan.highlight ? S : 'rgba(255,255,255,0.1)',
                  color: plan.highlight ? '#0A1628' : '#fff',
                  fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
                  padding: '4px 14px', borderRadius: 20, whiteSpace: 'nowrap',
                }}>
                  {plan.badge}
                </div>
              )}

              <div style={{ marginBottom: 24 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px', color: plan.highlight ? S : '#fff' }}>
                  {plan.name}
                </h2>
                <p style={{ fontSize: 13, color: G, margin: '0 0 20px', lineHeight: 1.6 }}>{plan.description}</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ fontSize: 36, fontWeight: 800, color: '#fff' }}>{plan.price}</span>
                  <span style={{ fontSize: 13, color: G }}>{plan.period}</span>
                </div>
              </div>

              {plan.ctaHref ? (
                <a href={plan.ctaHref} style={{
                  display: 'block', textAlign: 'center', textDecoration: 'none',
                  padding: '12px 0', borderRadius: 10, fontWeight: 700, fontSize: 14, marginBottom: 28,
                  background: 'rgba(255,255,255,0.06)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}>
                  {plan.cta}
                </a>
              ) : (
                <button
                  onClick={() => navigate(plan.ctaRoute)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'center',
                    padding: '12px 0', borderRadius: 10, fontWeight: 700, fontSize: 14, marginBottom: 28,
                    background: plan.highlight ? S : 'rgba(255,255,255,0.06)',
                    color: plan.highlight ? '#0A1628' : '#fff',
                    border: plan.highlight ? 'none' : '1px solid rgba(255,255,255,0.12)',
                    cursor: 'pointer',
                  }}
                >
                  {plan.cta}
                </button>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plan.features.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flexShrink: 0, marginTop: 1 }}><CheckIcon /></div>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
                {plan.limitations.map(l => (
                  <div key={l} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flexShrink: 0, marginTop: 1 }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </div>
                    <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.25)', lineHeight: 1.5 }}>{l}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* FAQ rapide */}
        <div style={{ marginTop: 64, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 48, textAlign: 'center' }}>
          <h3 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>Des questions ?</h3>
          <p style={{ color: G, fontSize: 14, margin: '0 0 20px' }}>
            Contactez-nous à{' '}
            <a href="mailto:contact@safeheir.fr" style={{ color: S, textDecoration: 'none' }}>
              contact@safeheir.fr
            </a>
            {' '}— réponse sous 24h.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/legal/cgu" style={{ color: G, textDecoration: 'none', fontSize: 13 }}>CGU</Link>
            <Link to="/legal/confidentialite" style={{ color: G, textDecoration: 'none', fontSize: 13 }}>Confidentialité</Link>
            <Link to="/" style={{ color: G, textDecoration: 'none', fontSize: 13 }}>Accueil</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
